import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { defineMessages } from 'react-intl';
import OkCancelModal from '@/shared/components/OkCancelModal';
import moment from 'moment';
import api from '@/shared/api';
import intlShape from '@/shared/utils/intlShape';
import DatePicker from '@/shared/components/input/DatePicker';
import { listItemMsg } from '@/shared/components/FormattedListValue';
import config from '@/config';
import { dynamicSort } from '@/shared/utils/dynamicSort';
import DataCacheService from '@/shared/services/DataCacheService';
import Checkbox from '@/shared/components/input/Checkbox';
import { isBeforeMinimum } from '@/shared/utils/validationHelpers';
import classNames from 'classnames';
import employeeProfileMessages from '@/shared/messages/employeeProfileMessages';
import AssignmentTemplateSelector from '@/shared/components/input/AssignmentTemplateSelector';
import { hasAssignments } from '@/shared/components/security/HasAssignments';
import guid from '@/shared/utils/guid';
import YearSelect from '@/shared/components/input/YearSelect';
import HasFeature from '@/shared/components/security/HasFeature';

const messages = defineMessages({
  UseAssignedToFromTemplate: 'Use assigned to from template',
  CreateTaskWithoutPlanning: 'Create task without planning',
  TaskTemplate: 'Task template',
  Schedule: 'Schedule',
  Deadline: 'Deadline',
  AssignedTo: 'Assigned to',
  AssignmentTemplate: 'Assignment template',
  Profiles: 'Profiles',
  Users: 'Users',
  DontAddTaskIfAlreadyExistsForRelation: 'Do not add the task if it already exists for relation',
  ReferenceYear: 'Reference year',
});

export default class CreatePlannedTaskModal extends Component {
  state = {
    templates: [],
    selectedTemplate: null,
    deadline: moment(),
    users: [],
    assignedUser: null,
    skipIfTaskAlreadyExists: false,
    assignmentTemplateId: guid.empty,
    year: moment().year(),
    showAssignmentSelector: false,
  };
  static propTypes = {
    show: PropTypes.bool.isRequired,
    intl: intlShape.isRequired,
    onSave: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
  };

  static defaultProps = {};

  constructor(props) {
    super(props);
    const { intl } = props;
    this.templateUser = {
      key: '0;0',
      assignedType: 0,
      assignedUserId: null,
      assignedProfile: null,
      label: intl.formatMessage(messages.UseAssignedToFromTemplate),
    };
    this.responsibleUser = {
      key: '1;1',
      assignedType: 1,
      assignedUserId: null,
      assignedProfile: 1,
      label: intl.formatMessage(employeeProfileMessages.Responsible),
    };
    this.assistantUser = {
      key: '1;2',
      assignedType: 1,
      assignedUserId: null,
      assignedProfile: 2,
      label: intl.formatMessage(employeeProfileMessages.Assistant),
    };
    this.assistantBackupUser = {
      key: '1;3',
      assignedType: 1,
      assignedUserId: null,
      assignedProfile: 3,
      label: intl.formatMessage(employeeProfileMessages.AssistantBackup),
    };
    this.associateUser = {
      key: '1;4',
      assignedType: 1,
      assignedUserId: null,
      assignedProfile: 4,
      label: intl.formatMessage(employeeProfileMessages.Associate),
    };
    this.billingUser = {
      key: '1;5',
      assignedType: 1,
      assignedUserId: null,
      assignedProfile: 5,
      label: intl.formatMessage(employeeProfileMessages.BillingUser),
    };
    this.seniorUser = {
      key: '1;6',
      assignedType: 1,
      assignedUserId: null,
      assignedProfile: 6,
      label: intl.formatMessage(employeeProfileMessages.SeniorUser),
    };
  }

  retrieveTaskTemplates = () => {
    const { intl } = this.props;

    return api.base.get(`${config.appUrls.tasks}/api/TaskTemplates`).then(({ data }) => {
      const translatedTemplates = data
        .map(t => ({
          ...t,
          label: intl.formatMessage(listItemMsg('taskTemplate', t.id, t.name)), // Map translation to label property
        }))
        .sort(dynamicSort('label'));

      let selectedTemplate = null;
      if (translatedTemplates && translatedTemplates.length > 0) {
        [selectedTemplate] = translatedTemplates;
      }

      this.setState(state => ({
        ...state,
        templates: translatedTemplates,
        selectedTemplate,
      }));
    });
  };

  retrieveUsers = () => {
    const cache = DataCacheService.getDataCache();

    if (!cache) {
      throw new Error('DataCache not found in RegistrationModal.jsx');
    }

    const users = [
      this.templateUser,
      this.assistantUser,
      this.responsibleUser,
      this.associateUser,
      this.assistantBackupUser,
      this.billingUser,
      this.seniorUser,
      ...(cache.user || [])
        .filter(x => x.isActive || !x.hidden)
        .map(u => ({
          key: `2;${u.userID}`,
          assignedType: 2,
          assignedUserId: u.userID,
          assignedProfile: 2,
          label: `${u.firstName} ${u.lastName}`,
          className: u.isActive ? '' : 'removed-user',
        }))
        .sort(dynamicSort('label')),
    ];
    this.setState({
      users,
      assignedUser: this.templateUser,
    });
  };

  componentDidMount() {
    this.retrieveTaskTemplates();
    this.retrieveUsers();
  }

  templateChanged = event => {
    const { templates } = this.state;
    const templateId = event.target.value;
    const template = templates.find(x => x.id === templateId);

    let selectedPeriodicity = null;

    if (template && template.periodicities) {
      [selectedPeriodicity] = template.periodicities;
    }

    this.setState(state => ({
      ...state,
      selectedTemplate: template,
      selectedPeriodicity,
    }));
  };

  assignedUserChanged = event => {
    const { users } = this.state;
    const key = event.target.value;

    const assignedUser = users.find(x => x.key === key);

    this.setState({
      assignedUser,
    });
  };

  assignmentTemplateChanged = id => this.setState({ assignmentTemplateId: id || guid.empty });

  isFormInvalid = () => {
    const { assignedUser, selectedTemplate, deadline } = this.state;
    if (!assignedUser || !assignedUser.key || !selectedTemplate || !deadline) return true;

    return false;
  };

  onSave = () => {
    const {
      deadline,
      selectedTemplate,
      assignedUser: { assignedType, assignedUserId, assignedProfile },
      skipIfTaskAlreadyExists,
      assignmentTemplateId,
      year,
    } = this.state;
    const { onSave } = this.props;

    const task = {
      deadline,
      taskTemplateId: selectedTemplate.id,
      assignedType,
      assignedUserId,
      assignedProfile,
      skipIfTaskAlreadyExists,
      assignmentTemplateId,
      year,
    };

    return onSave(task);
  };

  render() {
    const { show, onClose, intl } = this.props;
    const {
      deadline,
      deadlineFocused,
      templates,
      selectedTemplate,
      assignedUser,
      users,
      skipIfTaskAlreadyExists,
      assignmentTemplateId,
      year,
    } = this.state;
    if (!show) return null;
    return (
      <OkCancelModal
        title={intl.formatMessage(messages.CreateTaskWithoutPlanning)}
        show={show}
        onClose={onClose}
        disabled={this.isFormInvalid()}
        onSave={() => this.onSave()}
      >
        <div className="form-horizontal">
          <div className="form-group">
            <label className="control-label col-sm-4">
              {intl.formatMessage(messages.TaskTemplate)}
            </label>
            <div className="col-sm-8">
              {templates && (
                <select
                  onChange={this.templateChanged}
                  className="form-control"
                  value={(selectedTemplate && selectedTemplate.id) || ''}
                >
                  {templates.map(template => (
                    <option key={template.id} value={template.id}>
                      {template.label}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          <div className={classNames('form-group', { 'has-error': !deadline })}>
            <label className="control-label col-sm-4">
              {intl.formatMessage(messages.Deadline)}
            </label>
            <div className="col-sm-4">
              <DatePicker
                placeholder={intl.formatMessage(messages.Deadline)}
                date={deadline}
                onDateChange={date =>
                  this.setState(state => ({
                    deadline: date,
                    year: date ? moment(date).year() : state.year,
                  }))
                }
                focused={deadlineFocused}
                onFocusChange={({ focused }) => this.setState({ deadlineFocused: focused })}
                id="taskDeadline"
                required
                block
                small
                isOutsideRange={isBeforeMinimum}
              />
            </div>
          </div>
          <HasFeature feature="ReferenceYear">
            <div className="form-group">
              <label className="control-label col-sm-4">
                {intl.formatMessage(messages.ReferenceYear)}
              </label>
              <div className="col-sm-4">
                <YearSelect
                  id="referenceYearField"
                  value={year}
                  onChange={value => this.setState({ year: value })}
                  className="form-control"
                  yearsInPast={2}
                  required
                />
              </div>
            </div>
          </HasFeature>
          {hasAssignments() && this.props.showAssignmentSelector && (
            <div className="form-group">
              <label className="control-label col-sm-4">
                {intl.formatMessage(messages.AssignmentTemplate)}
              </label>
              <div className="col-sm-8">
                <AssignmentTemplateSelector
                  value={assignmentTemplateId}
                  onChange={this.assignmentTemplateChanged}
                  showDefaultAssignment
                  placeholder={null}
                />
              </div>
            </div>
          )}
          <div className="form-group">
            <label className="control-label col-sm-4">
              {intl.formatMessage(messages.AssignedTo)}
            </label>
            <div className="col-sm-8">
              <select
                onChange={this.assignedUserChanged}
                className="form-control"
                value={assignedUser ? assignedUser.key : '0;0'}
              >
                <option value={this.templateUser.key}>{this.templateUser.label}</option>
                <optgroup label={intl.formatMessage(messages.Profiles)}>
                  <option value={this.assistantUser.key}>{this.assistantUser.label}</option>
                  <option value={this.responsibleUser.key}>{this.responsibleUser.label}</option>
                  <option value={this.associateUser.key}>{this.associateUser.label}</option>
                  <option value={this.assistantBackupUser.key}>
                    {this.assistantBackupUser.label}
                  </option>
                  <option value={this.billingUser.key}>{this.billingUser.label}</option>
                  <option value={this.seniorUser.key}>{this.seniorUser.label}</option>
                </optgroup>
                <optgroup label={intl.formatMessage(messages.Users)}>
                  {users
                    .filter(x => x.assignedType === 2)
                    .map(u => (
                      <option key={u.key} value={u.key} className={u.className}>
                        {u.label}
                      </option>
                    ))}
                </optgroup>
              </select>
            </div>
          </div>

          <div className="form-group">
            <Checkbox
              extraClass="col-sm-offset-4 col-sm-8"
              id="overrideExistingSchedule"
              value={skipIfTaskAlreadyExists}
              onClick={() => this.setState({ skipIfTaskAlreadyExists: !skipIfTaskAlreadyExists })}
              text={intl.formatMessage(messages.DontAddTaskIfAlreadyExistsForRelation)}
            />
          </div>
        </div>
      </OkCancelModal>
    );
  }
}
